/* eslint-disable no-irregular-whitespace */
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Content, CreateButton, FilterButton } from './style';
import { LeftSide, OptionsButton, RightSide, SectionName, SubHeaderContainer, Tab, TabsDiv, RefreshIcon, FilterWrapper } from '../../../components/styles/subheader-style';
import { Filter as FilterIcon } from 'lucide-react'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { stockHeaders, movementHeaders } from '../Stock/utils'
import { productionFashionMovementHeaders } from '../ProductionFashion';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext'
import Plus from '../../../assets/production-flow/whiteplus.svg'

import { Outlet, useLocation, useNavigate, useOutletContext, useMatch } from 'react-router-dom';
import { TypeMainOutlet } from '..';
import movement from '../../../assets/stock/TableMovementHeader/refresh-active.svg';
import timeFast from '../../../assets/production-flow/time-fast.svg';
import FieldsManager, { FieldsManagerType } from '../../../components/FieldManager';
import { tableFields } from '../../../components/FieldManager/table-fields';
import WithoutSelectedFields from '../../SystemErrors/WithoutSelectedFields';
import LoadMoreButton from '../../../components/LoadMoreButton';
import arrow from '../../../assets/stock/LoadMoreButton/arrow-down.svg';
import filter from '../../../assets/stock/TableMovementHeader/filter.svg';
import refresh_act from '../../../assets/refresh-active.svg';
import TableFiltersContext from '../../../contexts/TableFiltersContext';
import SectionContext from '../../../contexts/SectionContext';
import { FilterTypesDiv, CompanyTypes } from '../../../components/Stock/TableMovementHeader/style';
import FiltersHeader from '../../../components/Stock/FiltersHeader';
import FiltersMenu from '../../../components/Stock/TableMovementHeader/FiltersMenu';
import ModuleKanban from '../Pipelines/ModuleKanban';

export interface ProductionOutletType {
  selectedStage: number;
  setSelectedStage: React.Dispatch<React.SetStateAction<number>>;
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  search: string;
  fields: FieldsManagerType;
  setFields: React.Dispatch<React.SetStateAction<FieldsManagerType>>;
  isdropdownOpen: boolean;
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  appliedFilters: Filter[]
}

const TAB_INDEX_PRODUCTION = 0;
const TAB_INDEX_MOVEMENT = 1;
const TAB_INDEX_BOARD = 2;

const tabs = [
  { path: '/panel/production', image: timeFast, name: 'Ordem de produção', index: TAB_INDEX_PRODUCTION },
  { path: '/panel/production/movement', image: movement, name: 'Movimentação', index: TAB_INDEX_MOVEMENT },
  { path: '/panel/production/board', image: movement, name: 'Funil', index: TAB_INDEX_BOARD },
];

const companyTypesIds = ['Pedido', 'Manual'];

const ProductionFlow = () => {
  const { getPreference, getEffectivePreference, setPreference, isTenantAdmin, isLoading: isPrefsLoading } = useUserPreferences();
  const useMyPrefsKey = 'useMyPreferences_tableFields';
  const useMyPrefs = getPreference<boolean>(useMyPrefsKey, false);
  const navigate = useNavigate();
  const movementMatch = useMatch('/panel/production/movement');
  const boardMatch = useMatch('/panel/production/board');
  const createMatch = useMatch('/panel/production/create');
  const editMatch = useMatch('/panel/production/edit/:id');

  const tabIndex = boardMatch ? TAB_INDEX_BOARD : (movementMatch ? TAB_INDEX_MOVEMENT : TAB_INDEX_PRODUCTION);
  const isCreateOrEditPage = createMatch || editMatch;

  const [isdropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<number>(1);
  const { search } = useOutletContext<TypeMainOutlet>();
  const fromLocalStorage = getEffectivePreference<FieldsManagerType[] | undefined>('tableFields');
  const [quantity, setQuantity] = useState(50);
  const [refresh, setRefresh] = useState(false);
  const { movementFilters, setMovementFilters, setStockFilters, movementTypeFilter, setMovementTypeFilter } = useContext(TableFiltersContext);
  const { stockSection } = useContext(SectionContext);
  const location = useLocation();

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const filterButtonRef = useRef<HTMLDivElement>(null);

  const orderFiltersState = usePersistedFilters('production_order')
  const movementFiltersState = usePersistedFilters('production_movement')

  const activeFiltersState = tabIndex === 1 ? movementFiltersState : orderFiltersState

  useEffect(() => {
    const mapped = orderFiltersState.appliedFilters.flatMap(f => {
      if (f.filterType === 'Intervalo' && f.value?.start && f.value?.end) {
        return [
          { field: f.label, type: 'É maior/igual que', text: f.value.start },
          { field: f.label, type: 'É menor/igual que', text: f.value.end }
        ]
      }
      let typeMapped = f.filterType
      if (f.filterType === 'equals') typeMapped = 'É igual a'
      else if (f.filterType === 'contains') typeMapped = 'Contém'
      else if (f.filterType === 'startsWith') typeMapped = 'Começa com'
      else if (f.filterType === 'endsWith') typeMapped = 'Acaba com'
      else if (f.filterType === 'not') typeMapped = 'É diferente de'
      else if (f.filterType === 'gt') typeMapped = 'É maior que'
      else if (f.filterType === 'lt') typeMapped = 'É menor que'
      else if (f.filterType === 'gte') typeMapped = 'É maior/igual que'
      else if (f.filterType === 'lte') typeMapped = 'É menor/igual que'
      else if (f.filterType === 'Antes de') typeMapped = 'É menor que'
      else if (f.filterType === 'Depois de') typeMapped = 'É maior que'
      else if (f.filterType === 'Igual a') typeMapped = 'É igual a'
      else if (f.filterType === 'Diferente de') typeMapped = 'É diferente de'
      else if (f.filterType === 'Maior que') typeMapped = 'É maior que'
      else if (f.filterType === 'Menor que') typeMapped = 'É menor que'

      let val = f.value
      if (f.fieldType === 'boolean') {
        val = f.value === 'true' || f.value === true
      }
      return [{ field: f.label, type: typeMapped, text: String(val) }]
    })
    setStockFilters(mapped)
  }, [orderFiltersState.appliedFilters, setStockFilters])

  useEffect(() => {
    const mapped = movementFiltersState.appliedFilters.flatMap(f => {
      if (f.filterType === 'Intervalo' && f.value?.start && f.value?.end) {
        return [
          { field: f.label, type: 'É maior/igual que', text: f.value.start },
          { field: f.label, type: 'É menor/igual que', text: f.value.end }
        ]
      }
      let typeMapped = f.filterType
      if (f.filterType === 'equals') typeMapped = 'É igual a'
      else if (f.filterType === 'contains') typeMapped = 'Contém'
      else if (f.filterType === 'startsWith') typeMapped = 'Começa com'
      else if (f.filterType === 'endsWith') typeMapped = 'Acaba com'
      else if (f.filterType === 'not') typeMapped = 'É diferente de'
      else if (f.filterType === 'gt') typeMapped = 'É maior que'
      else if (f.filterType === 'lt') typeMapped = 'É menor que'
      else if (f.filterType === 'gte') typeMapped = 'É maior/igual que'
      else if (f.filterType === 'lte') typeMapped = 'É menor/igual que'
      else if (f.filterType === 'Antes de') typeMapped = 'É menor que'
      else if (f.filterType === 'Depois de') typeMapped = 'É maior que'
      else if (f.filterType === 'Igual a') typeMapped = 'É igual a'
      else if (f.filterType === 'Diferente de') typeMapped = 'É diferente de'
      else if (f.filterType === 'Maior que') typeMapped = 'É maior que'
      else if (f.filterType === 'Menor que') typeMapped = 'É menor que'

      let val = f.value
      if (f.fieldType === 'boolean') {
        val = f.value === 'true' || f.value === true
      }
      return [{ field: f.label, type: typeMapped, text: String(val) }]
    })
    setMovementFilters(mapped)
  }, [movementFiltersState.appliedFilters, setMovementFilters])



  const [fields, setFields] = useState<FieldsManagerType | undefined>(() => {
    const tableFieldsFromLocalStorage: FieldsManagerType[] = fromLocalStorage
      ? (typeof fromLocalStorage === 'string' ? JSON.parse(fromLocalStorage) : fromLocalStorage)
      : tableFields;
    const currentSection = tabIndex === 1 ? 'movement' : 'stock';
    const foundFields = tableFieldsFromLocalStorage.find(field => field.tableSection === currentSection);
    const defaultFromTable = tableFields.find(field => field.tableSection === currentSection);
    

    return (foundFields && foundFields.whichFieldsToShow.some(field => field.checked)) 
      ? foundFields 
      : defaultFromTable;
  });

  const tableFieldsJson: FieldsManagerType[] = fromLocalStorage
    ? (typeof fromLocalStorage === 'string' ? JSON.parse(fromLocalStorage) : fromLocalStorage)
    : tableFields;

  const handleSaveFields = useCallback((value: React.SetStateAction<FieldsManagerType>) => {
    setFields((prev) => {
      const newFields = typeof value === 'function' ? (value as any)(prev) : value

      const currentPreferences = fromLocalStorage || tableFields;
      const toLocalStorage = currentPreferences.map((table: FieldsManagerType) => {
        const currentSection = tabIndex === 1 ? 'movement' : 'stock';
        if (table.tableSection === currentSection) {
          return { ...table, whichFieldsToShow: newFields.whichFieldsToShow };
        }
        return table;
      });

      setTimeout(() => {
        setPreference('tableFields', toLocalStorage);
        if (!isTenantAdmin) {
          setPreference(useMyPrefsKey, true);
        }
      }, 0);
      return newFields
    })
  }, [fromLocalStorage, tabIndex, isTenantAdmin, useMyPrefsKey, setPreference]);

  useEffect(() => {
    if (isPrefsLoading) return;
    const currentSection = tabIndex === 1 ? 'movement' : 'stock';
    const foundFields = tableFieldsJson.find(field => field.tableSection === currentSection);
    const defaultFromTable = tableFields.find(field => field.tableSection === currentSection);

    const fieldsToUse = (foundFields && foundFields.whichFieldsToShow.some(field => field.checked)) 
      ? foundFields 
      : defaultFromTable;
      
    if (fieldsToUse && JSON.stringify(fieldsToUse) !== JSON.stringify(fields)) {
      setFields(fieldsToUse);
    }
  }, [tabIndex, isPrefsLoading, fromLocalStorage]);

  const handleSetTypes = (id: string) => {
    if (movementTypeFilter.includes(id)) {
      if (movementTypeFilter.split(',').length === 1) {
        return;
      }
      setMovementTypeFilter(
        movementTypeFilter
          .split(',')
          .filter((item: string) => item !== id)
          .join(',')
      );
    } else {
      setMovementTypeFilter(movementTypeFilter ? `${movementTypeFilter},${id}` : id);
    }
  };

  const handleButtonCreate = () => {
    navigate('/panel/production/create');
  };

  if (!fields) {
    return null;
  }

  return (
    <>
      {!isCreateOrEditPage && !boardMatch && (
        <>
          <SubHeaderContainer>
            <LeftSide>
              <SectionName>Produção</SectionName>
              <TabsDiv>
                {tabs.map((tab) => (
                  <Tab
                    key={tab.index}
                    $isFirst={tab.index === 0}
                    $isLast={tab.index === tabs.length - 1}
                    $isActive={tab.index === tabIndex}
                    onClick={() => navigate(tab.path)}
                  >
                    {tab.index === TAB_INDEX_BOARD ? (
                      <FilterIcon size={12.8} style={{ marginRight: 7 }} />
                    ) : (
                      <img src={tab.image} alt={`${tab.path}-icon`} />
                    )}
                    {tab.name}
                  </Tab>
                ))}
              </TabsDiv>
              {!isTenantAdmin && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: '#696969', marginLeft: '16px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useMyPrefs}
                    onChange={(e) => setPreference(useMyPrefsKey, e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#0090D3', cursor: 'pointer' }}
                  />
                  Utilizar minhas preferências
                </label>
              )}
            </LeftSide>
            <RightSide>
              {tabIndex !== TAB_INDEX_BOARD && (
                <>
                  <FilterWrapper>
                    <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                      <FilterIcon size={10} />
                    </OptionsButton>
                    {isFilterMenuOpen && (
                      <FilterMenu
                        headers={tabIndex === 1 ? productionFashionMovementHeaders : stockHeaders}
                        onApplyFilter={(f) => {
                          activeFiltersState.handleApplyFilter(f)
                          setIsFilterMenuOpen(false)
                        }}
                        onClose={() => setIsFilterMenuOpen(false)}
                        anchorRef={filterButtonRef}
                        filters={activeFiltersState.appliedFilters}
                      />
                    )}
                  </FilterWrapper>

                  <CreateButton onClick={handleButtonCreate}>
                    <img src={Plus} alt="plus-icon" />
                    Ordem de produção
                  </CreateButton>
                </>
              )}
            </RightSide>
          </SubHeaderContainer>
          {tabIndex !== TAB_INDEX_BOARD && (
            <AppliedFiltersDisplay
              filters={activeFiltersState.appliedFilters}
              onRemoveFilter={activeFiltersState.handleRemoveFilter}
              onClearAll={activeFiltersState.handleClearAll}
            />
          )}
        </>
      )}

      {tabIndex === TAB_INDEX_BOARD && !isCreateOrEditPage ? (
        <ModuleKanban
          moduleType="production"
          moduleName="Produção"
          subModule="steel"
          headerLeftContent={
            <>
              <SectionName>Produção</SectionName>
              <TabsDiv>
                {tabs.map((tab) => (
                  <Tab
                    key={tab.index}
                    $isFirst={tab.index === 0}
                    $isLast={tab.index === tabs.length - 1}
                    $isActive={tab.index === tabIndex}
                    onClick={() => navigate(tab.path)}
                  >
                    {tab.index === TAB_INDEX_BOARD ? (
                      <FilterIcon size={12.8} style={{ marginRight: 7 }} />
                    ) : (
                      <img src={tab.image} alt={`${tab.path}-icon`} />
                    )}
                    {tab.name}
                  </Tab>
                ))}
              </TabsDiv>
            </>
          }
        />
      ) : (
        <Content>
          {fields.whichFieldsToShow.filter((field) => field.checked).length === 0 ? (
            <WithoutSelectedFields dropdown={setIsDropdownOpen} />
          ) : (
            <Outlet
              context={{
                selectedStage,
                setSelectedStage,
                tabIndex,
                setTabIndex: () => {},
                search,
                fields,
                setFields: handleSaveFields,
                isdropdownOpen,
                setIsDropdownOpen,
                quantity,
                setQuantity,
                refresh,
                setRefresh,
                appliedFilters: activeFiltersState.appliedFilters,
              }}
            />
          )}
        </Content>
      )}
    </>
  );
};

export default ProductionFlow;